import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FileStillUploadingError, FileUploadFailedError, stagedUploadId } from '@ee/common/custom-field';
import { CustomFieldType } from '@ee/common/enums';
import { Eviction, IntakeUpload, Tenant } from '@ee/common/models';
import { FieldDefinitionView, LayoutPageView } from './intake-layout.model';
import { placementsOf } from './intake-form-controls';

/**
 * The v2 form's answers, in the shape the submission endpoint expects.
 *
 * The wizard holds one flat group keyed by storage key, because that is what the layout describes. The
 * endpoint wants `eviction_details` with the built-in fields at the top level and everything a firm
 * invented under `custom_fields`, plus the account and business details as their own objects — the same
 * shape `IntakeEvictionFormService.generateEvictionDetails` produces by merging its five step groups.
 *
 * Pure, and tested, because this is the filing. A mapper that quietly drops a field produces a case
 * that is missing something nobody notices until a court does.
 */

/** Page one's questions, which create the client's account rather than describing the case. */
const USER_KEYS = ['first_name', 'last_name', 'email', 'phone_number'] as const;
const CLIENT_KEYS = ['company_name', 'street_address_1', 'street_address_2', 'city', 'state', 'zip_code'] as const;

export interface SubmissionParts {
  evictionDetails: Eviction;
  /**
   * Loose records rather than `User` / `Organization`.
   *
   * Only the handful of keys page one asks for are ever set, and typing them as the full entities would
   * claim an id, a role and a subscription this has no business inventing.
   */
  userDetails?: Record<string, unknown>;
  clientDetails?: Record<string, unknown>;
  /** One per answered file placement, typed by its key: the staged uploads the endpoint attaches. */
  uploads: IntakeUpload[];
}

function scalar(value: unknown): unknown {
  return value === '' ? null : value;
}

/**
 * Which keys are the product's and which the firm's.
 *
 * From `FieldDefinitionView.origin`, which is the only place that knows. Guessing by prefix is how a
 * firm's field called `property_note` ends up at the top level of `Eviction`, where nothing reads it.
 */
function isCustom(key: string, definitions: Map<string, FieldDefinitionView>): boolean {
  return definitions.get(key)?.origin === 'CUSTOM';
}

/**
 * The tenants, one object per panel the client filled in.
 *
 * Empty rows are dropped: "Add another tenant" then changing your mind leaves a group with nothing in
 * it, and submitting it names a tenant with no name on the complaint.
 */
function tenantsOf(form: FormGroup): Tenant[] {
  const array = form.get('tenants') as FormArray | null;
  return ((array?.controls ?? []) as FormGroup[])
    .map(group => group.getRawValue() as Record<string, unknown>)
    .filter(tenant => Object.values(tenant).some(value => value !== null && value !== undefined && value !== ''))
    .map(tenant => tenant as unknown as Tenant);
}

/**
 * A file answer as the staged upload the server attaches.
 *
 * The control holds the `File` (`ee-uploader` writes an array of one) and the submit is JSON, where a
 * `File` is `{}`. The upload id sits beside the control (`staged-upload.ts`); a file without one is still
 * uploading, or failed, and is refused rather than sent without it — the server would drop it silently.
 */
function uploadOf(key: string, control: AbstractControl | null, definition: FieldDefinitionView | undefined): IntakeUpload | null {
  const value = control?.getRawValue();
  const file = Array.isArray(value) ? value[0] : value;
  if (!control || !(file instanceof File)) {
    return null;
  }
  const label = definition?.label || key;
  if (control.hasError('uploadFailed')) {
    throw new FileUploadFailedError(label);
  }
  const uploadId = stagedUploadId(control);
  if (!uploadId) {
    throw new FileStillUploadingError(label, 'submit');
  }
  return { type: key, upload_id: uploadId };
}

export function toSubmissionParts(
  pages: LayoutPageView[],
  form: FormGroup,
  definitions: Map<string, FieldDefinitionView>,
  options: { includeAccount: boolean } = { includeAccount: true }
): SubmissionParts {
  const values = form.getRawValue() as Record<string, unknown>;

  const builtIn: Record<string, unknown> = {};
  const custom: Record<string, unknown> = {};
  const uploads: IntakeUpload[] = [];

  // Only what the form actually placed. Walking the raw values instead would submit a control left
  // behind by a field the firm has since removed.
  placementsOf(pages).forEach(item => {
    const keys =
      item.kind === 'COMPOSITE' && item.composite_type
        ? Object.keys(values).filter(key => key.startsWith('property_'))
        : [item.field_key ?? ''];

    keys
      .filter(key => key && key in values)
      .forEach(key => {
        if (USER_KEYS.includes(key as (typeof USER_KEYS)[number])) {
          return;
        }
        if (CLIENT_KEYS.includes(key as (typeof CLIENT_KEYS)[number])) {
          return;
        }
        if (definitions.get(key)?.type === CustomFieldType.FILE_UPLOAD) {
          const upload = uploadOf(key, form.get(key), definitions.get(key));
          if (upload && !uploads.some(u => u.upload_id === upload.upload_id)) {
            uploads.push(upload);
          }
          return;
        }
        (isCustom(key, definitions) ? custom : builtIn)[key] = scalar(values[key]);
      });
  });

  /*
   * `plaintiff` is the intake key and `company_name` is what `Eviction` carries for the same thing —
   * the two disagree in the codebase and the layout uses `plaintiff` because that is what a document
   * template can address. Translated here rather than anywhere else, so only one place knows.
   */
  if ('plaintiff' in builtIn) {
    builtIn['company_name'] = builtIn['plaintiff'];
    delete builtIn['plaintiff'];
  }

  const evictionDetails = {
    ...builtIn,
    tenants: tenantsOf(form),
    custom_fields: custom
  } as unknown as Eviction;

  if (!options.includeAccount) {
    return { evictionDetails, uploads };
  }

  const pick = (keys: readonly string[]): Record<string, unknown> =>
    keys.reduce<Record<string, unknown>>((acc, key) => {
      if (key in values) {
        acc[key] = scalar(values[key]);
      }
      return acc;
    }, {});

  const userDetails = pick(USER_KEYS);
  const clientDetails = pick(CLIENT_KEYS);

  return {
    evictionDetails,
    uploads,
    // Omitted rather than sent empty: the server synthesizes them for a signed-in client, and an object
    // of nulls is not the same as "not supplied".
    userDetails: Object.keys(userDetails).length ? userDetails : undefined,
    clientDetails: Object.keys(clientDetails).length ? clientDetails : undefined
  };
}
